import { useRef, useEffect } from "react";

import { LoadingSpinner, Logo } from "@newsela/angelou";
import dayjs from "dayjs";

import useImplementationGuideCompletionRateGet from "@/components/ImplementationGuide/hooks/useImplementationGuideCompletionRateGet";
import useImplementationGuideSummaryGet from "@/components/ImplementationGuide/hooks/useImplementationGuideSummaryGet";
import { FormSlug } from "@/components/ImplementationGuide/types";
import { CardResource } from "@/components/ImplementationGuide/types/CardResource.interface";
import { ProfessionalLearningFormData } from "@/components/ImplementationGuide/types/ImplementationGuideFormData.interface";
import { getSortedForms } from "@/components/ImplementationGuide/utils";
import getFormCompletionRate from "@/components/ImplementationGuide/utils/getFormCompletionRate";
import getFormResources from "@/components/ImplementationGuide/utils/getFormResources";
import getFormResponsesForKeyTeamMembersSection from "@/components/ImplementationGuide/utils/getFormResponsesForKeyTeamMembersSection";
import capitalize from "@/utils/capitalize";
import useImplementationGuidePDFDownload from "../hooks/useImplementationGuidePDFDownload";
import HeaderActionButtons from "../components/HeaderActionButtons";
import {
  pageHeadingContainerCss,
  pageSubTitleCss,
  pageTitleCss,
  pdfLogoContainerCss,
  pageContentContainerCss,
  PDFContainerCss,
  updatedOnTextCss,
} from "./ResultsPage.style";
import FormSection from "./FormSection";

interface ResultsPageProps {
  districtUniqueId: string;
  districtName: string;
  isPDFOutput?: boolean;
}

const ResultsPage = (props: ResultsPageProps) => {
  const pageContentRef = useRef<HTMLDivElement>(null);

  const { districtName, districtUniqueId, isPDFOutput = false } = props;
  const { data: summaryData, loading: loadingSummaryData } =
    useImplementationGuideSummaryGet(districtUniqueId ?? "");
  const { data: completionRateData, loading: loadingCompletionRate } =
    useImplementationGuideCompletionRateGet(districtUniqueId ?? "");
  const forms = getSortedForms;

  const pageTitle = `Admin quick start ${capitalize(districtName)}`;
  const pageSubtitleForPDFOutput =
    "Ensure success with Newsela by using the checklist and resources below.";

  const isLoading = loadingSummaryData || loadingCompletionRate;
  const hasSubscription = summaryData
    ? (summaryData["professional-learning"] as ProfessionalLearningFormData)
        .has_professional_learning_subscription
    : false;

  const downloadPDF = useImplementationGuidePDFDownload({
    contentRef: pageContentRef,
    documentTitle: pageTitle,
  });

  useEffect(() => {
    if (isPDFOutput && !isLoading) {
      void downloadPDF();
    }
  }, [isPDFOutput, isLoading]);

  const renderPageHeaderForPDFOutput = () => {
    return (
      <div>
        <div css={pdfLogoContainerCss}>
          <Logo __overrideHeight="50px" __overrideFullWidth="250px" />
        </div>
        <div css={pageHeadingContainerCss}>
          <h1 css={pageTitleCss}>{pageTitle}</h1>
          <p css={pageSubTitleCss}>{pageSubtitleForPDFOutput}</p>
        </div>
        <HeaderActionButtons isPDFOutput areFormsCompleted />
      </div>
    );
  };

  if (isLoading) {
    return <LoadingSpinner />;
  }

  return (
    <div ref={pageContentRef} css={isPDFOutput && PDFContainerCss}>
      {isPDFOutput && renderPageHeaderForPDFOutput()}
      {completionRateData && (
        <>
          <div css={updatedOnTextCss}>
            This guide was updated on{" "}
            {dayjs(completionRateData.last_modified_date).format(
              "MMMM D, YYYY",
            )}
          </div>
          <div css={pageContentContainerCss}>
            {forms.map((form) => {
              const { completionRate, incompleteQuestions } =
                getFormCompletionRate(form, completionRateData);
              const isCompleted = completionRate === 1;
              let isResourceValid = (resource: CardResource) =>
                resource !== undefined;
              if (form.slug === FormSlug.ProfessionalLearning) {
                isResourceValid = (resource: CardResource) =>
                  (resource.response_option === "Paid" && hasSubscription) ||
                  (resource.response_option === "Unpaid" && !hasSubscription);
              }
              const resources = summaryData
                ? form.slug === FormSlug.KeyTeamMembers
                  ? getFormResponsesForKeyTeamMembersSection(summaryData)
                  : getFormResources(summaryData, form, isResourceValid)
                : [];

              return (
                <FormSection
                  key={form.title}
                  title={form.title}
                  subtitle={form.subtitle}
                  editButtonUrl={`/chiron/implementation-guide/district/${districtUniqueId}/form/${form.slug as string}`}
                  image={form.form_image}
                  isCompleted={isCompleted}
                  resources={resources}
                  numberOfIncompleteQuestions={incompleteQuestions}
                  isPDFOutput={isPDFOutput}
                />
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default ResultsPage;
